import type { RootState } from '@app/store';

import { asyncThunkCreator, buildCreateSlice, PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

import { addProductToCart, addProductToCartThunk } from '@screens/Cart';
import { addToComparisonThunk } from '@screens/Comparison';
import { addProductFromComparison } from '@screens/Comparison/api/addProductToComparison/addProductFromComparison';
import { addProductToFavorites } from '@screens/Favorites/api/addProductToFavorites/addProductToFavorites';
import { DEFAULT_ERROR_TEXT } from '@shared/constants/text';
import { CookiesItems } from '@shared/types/enums/global.enum';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { PendingActions } from '@shared/types/enums/pendingActions.enum';
import { getCookiesProductsForUser } from '@shared/utils/cookiesUtils/cookiesUtils';
import { validatePhoneNumber } from '@shared/utils/fieldValidators/fieldValidators';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';
import { ProductsForUser } from '@widgets/Auth/model/types/productsForUser';

import { checkAuthCode } from '../../api/checkAuthCode/checkAuthCode';
import { getAuthCodeForLogin } from '../../api/getAuthCodeForLogin/getAuthCodeForLogin';
import { AuthPrevStepType, AuthStateType, AuthStepType, RejectErrorType } from '../../model/types/authState';

const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator }
});

export const initialAuthState: AuthStateType = {
  modalIsOpen: false,
  step: 'login',
  prevStep: 'login',
  isLoading: false,
  errorText: '',
  linkAfterLogin: '',
  pendingAction: null,
  phone: '',
  phoneError: '',
  verifyCode: {
    code: '',
    codeSendingTime: 0,
    error: '',
    timeIsEnd: false
  }
};

const SECONDS = 60;

export const authSlice = createAppSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: (create) => ({
    openAuthModal: create.reducer(
      (
        state,
        action: PayloadAction<{ linkAfterLogin?: string; pendingAction?: AuthStateType['pendingAction'] } | undefined>
      ) => {
        state.modalIsOpen = true;
        state.step = 'login';

        if (action.payload?.linkAfterLogin) {
          state.linkAfterLogin = action.payload.linkAfterLogin;
        }

        if (action.payload?.pendingAction) {
          state.pendingAction = action.payload.pendingAction;
        }
      }
    ),
    closeAuthModal: create.reducer((state) => {
      return { ...initialAuthState, step: state.step };
    }),
    changeStep: create.reducer((state, action: PayloadAction<AuthStepType>) => {
      state.step = action.payload;
      state.errorText = '';
    }),
    changePrevStep: create.reducer((state, action: PayloadAction<AuthPrevStepType>) => {
      state.prevStep = action.payload;
    }),
    changePhone: create.reducer((state, action: PayloadAction<{ value: string; key: string }>) => {
      const { value } = action.payload;

      state.phone = value;
      state.phoneError = '';
    }),
    changeCode: create.reducer((state, action: PayloadAction<string>) => {
      state.verifyCode.code = action.payload;
      state.verifyCode.error = '';
    }),
    setCodeSendingTime: create.reducer((state, action: PayloadAction<string>) => {
      state.verifyCode.codeSendingTime = +action.payload;
    }),
    setTimeIsEnd: create.reducer((state, action: PayloadAction<boolean>) => {
      state.verifyCode.timeIsEnd = action.payload;
    }),
    getVerifyCodeForLoginByPhoneNumber: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const identifier = store.auth.phone.trim();

        const isValid = validatePhoneNumber(identifier);

        if (!isValid) {
          return thunkAPI.rejectWithValue({ type: 'validation', phone: 'Некорректный номер телефона' });
        }

        const res = await getAuthCodeForLogin({ identifier });

        if (!res.ok) {
          return thunkAPI.rejectWithValue({
            type: 'server',
            message: res?.message ? res.message.toString() : DEFAULT_ERROR_TEXT
          });
        }

        const currentTime = new Date().getTime() + SECONDS * 1000;

        return {
          data: {
            ...res,
            codeSendingTime: currentTime
          }
        };
      },
      {
        pending: (state) => {
          state.isLoading = true;
        },
        rejected: (state, action) => {
          if (action.payload instanceof Object) {
            const error = action.payload as RejectErrorType;
            if (error.type === 'validation') {
              state.phoneError = error?.phone ? error.phone : '';
              state.errorText = '';
            }
            if (error.type === 'server') {
              state.errorText = error?.message ? error.message : DEFAULT_ERROR_TEXT;
            }
          }
        },
        fulfilled: (state, action) => {
          state.step = 'verify';
          state.verifyCode.codeSendingTime = action.payload.data.codeSendingTime;
          state.verifyCode.timeIsEnd = false;
          state.errorText = '';
        },
        settled: (state) => {
          state.isLoading = false;
        }
      }
    ),
    sendVerifyCode: create.asyncThunk(
      async (_: undefined, thunkAPI) => {
        const store = thunkAPI.getState() as RootState;
        const code = store.auth.verifyCode.code.trim();
        const identifier = store.auth.phone.trim();
        const linkAfterLogin = store.auth.linkAfterLogin;
        const { payload: payloadPendingAction, type: typePendingAction } = store.auth.pendingAction || {};

        const isValid = code !== '';

        if (!isValid) {
          return thunkAPI.rejectWithValue({ code: 'Поле не может быть пустым', type: 'validation' });
        }

        const data = await checkAuthCode({ identifier, code });

        if (!data.ok) {
          return thunkAPI.rejectWithValue({
            type: 'server',
            message: data?.message ? data.message.toString() : DEFAULT_ERROR_TEXT
          });
        }

        const cookiesFavouritesCount = getCookiesProductsForUser<string[]>('favouritesCount');
        const cookiesComparesCount = getCookiesProductsForUser<string[]>('comparesCount');
        const cookiesCartCount = getCookiesProductsForUser<ProductsForUser['cartCount']>('cartCount');

        if (cookiesFavouritesCount.length) {
          await addProductToFavorites(cookiesFavouritesCount);
        }

        if (cookiesComparesCount.length) {
          await addProductFromComparison(cookiesComparesCount);
        }

        if (cookiesCartCount.length) {
          await addProductToCart(cookiesCartCount.map((item) => ({ ...item, quantity: +item.quantity })));
        }

        if (linkAfterLogin !== '') {
          window.location.href = linkAfterLogin;
        }

        if (
          typePendingAction === PendingActions.ADD_TO_CART &&
          payloadPendingAction &&
          typeof payloadPendingAction !== 'string'
        ) {
          thunkAPI.dispatch(addProductToCartThunk(payloadPendingAction));
        }

        if (typePendingAction === PendingActions.ADD_TO_COMPARISON && typeof payloadPendingAction === 'string') {
          thunkAPI.dispatch(addToComparisonThunk(payloadPendingAction));
        }

        return {
          data
        };
      },
      {
        pending: (state) => {
          state.isLoading = true;
        },
        rejected: (state, action) => {
          if (action.payload instanceof Object) {
            const error = action.payload as RejectErrorType;
            if (error.type === 'validation') {
              state.verifyCode.error = error?.code ? error.code : '';
            }
            if (error.type === 'server') {
              state.verifyCode.error = error?.message ? error.message : DEFAULT_ERROR_TEXT;
            }
          }
        },
        fulfilled: (state) => {
          state.modalIsOpen = false;
          state.errorText = '';

          state.verifyCode.timeIsEnd = true;
          state.verifyCode.code = '';
          state.verifyCode.codeSendingTime = 0;
          state.verifyCode.error = '';

          Cookies.remove(CookiesItems.PRODUCTS_FOR_USER);

          sendMetrics(Metrics.AUTHORIZATION);
        },
        settled: (state) => {
          state.isLoading = false;
        }
      }
    )
  })
});

export const {
  openAuthModal,
  closeAuthModal,
  changeStep,
  changeCode,
  changePhone,
  getVerifyCodeForLoginByPhoneNumber,
  setTimeIsEnd,
  sendVerifyCode,
  changePrevStep
} = authSlice.actions;

export const authReducer = authSlice.reducer;
